import { useState, useCallback, useRef, useEffect } from 'react'
import supabase from '../utils/supabaseClient'
import logger from '../utils/logger'

/**
 * Custom hook for managing Supabase realtime channel lifecycle
 * Uses refs for handlers to prevent dependency-triggered re-subscriptions
 * @param {Object} config - Configuration object
 * @param {string} config.pollId - The poll short code for logging
 * @param {string} config.pollUuid - The poll UUID for Supabase filter
 * @param {string} config.deviceId - Current device ID for presence tracking
 * @param {Object} config.choiceHandlers - Choice event handlers
 * @param {Object} config.voteHandlers - Vote event handlers (with process* methods)
 * @param {Object} config.presenceHandlers - Presence event handlers
 * @returns {Object} Channel state and control functions
 */
function useRealtimeChannel({
    pollId,
    pollUuid,
    deviceId,
    choiceHandlers,
    voteHandlers,
    presenceHandlers
}) {
    const [realtimeStatus, setRealtimeStatus] = useState('disconnected')
    const subscriptionRef = useRef(null)
    const deviceIdRef = useRef(deviceId)

    // Store handlers in refs to avoid dependency issues
    // Callbacks in the channel will read from these refs, so they always get the latest handlers
    const choiceHandlersRef = useRef(choiceHandlers)
    const voteHandlersRef = useRef(voteHandlers)
    const presenceHandlersRef = useRef(presenceHandlers)

    // Keep refs in sync with latest handlers
    useEffect(() => {
        deviceIdRef.current = deviceId
    }, [deviceId])

    useEffect(() => {
        choiceHandlersRef.current = choiceHandlers
    }, [choiceHandlers])

    useEffect(() => {
        voteHandlersRef.current = voteHandlers
    }, [voteHandlers])

    useEffect(() => {
        presenceHandlersRef.current = presenceHandlers
    }, [presenceHandlers])

    const cleanupRealtimeSubscription = useCallback(() => {
        if (subscriptionRef.current) {
            logger.info('Cleaning up realtime subscription', { pollId })
            supabase.removeChannel(subscriptionRef.current)
            subscriptionRef.current = null
            setRealtimeStatus('disconnected')
        }
    }, [pollId])

    const setupRealtimeSubscription = useCallback(() => {
        if (!pollId || !pollUuid) return

        // Guard against duplicate subscriptions
        if (subscriptionRef.current) {
            logger.debug('Realtime subscription already exists, skipping setup', { pollId })
            return
        }

        logger.info('Setting up realtime subscription', {
            pollId,
            pollUuid
        })
        setRealtimeStatus('connecting')

        // Subscribe to changes for this poll using the UUID
        // All handlers are accessed via refs, so this callback doesn't need handler dependencies
        const channel = supabase
            .channel(`poll-${pollUuid}`)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'polls',
                    filter: `id=eq.${pollUuid}`
                },
                (payload) => {
                    if (payload.new) {
                        logger.info('Realtime: Poll updated', { pollId, newTitle: payload.new.title?.substring(0, 30) })
                        choiceHandlersRef.current.handlePollUpdated(payload.new)
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'choices',
                    filter: `poll_id=eq.${pollUuid}`
                },
                (payload) => {
                    logger.info('Realtime: New choice added', { pollId, choiceId: payload.new.id })
                    choiceHandlersRef.current.handleNewChoice(payload.new)
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'choices',
                    filter: `poll_id=eq.${pollUuid}`
                },
                (payload) => {
                    const deletedChoiceId = payload.old?.id
                    if (deletedChoiceId) {
                        logger.info('Realtime: Choice deleted', { pollId, choiceId: deletedChoiceId })
                        choiceHandlersRef.current.handleChoiceDeleted(deletedChoiceId)
                    } else {
                        logger.warn('Realtime: Choice DELETE event missing choice id', { pollId, payload: payload.old })
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'choices',
                    filter: `poll_id=eq.${pollUuid}`
                },
                (payload) => {
                    if (payload.new) {
                        logger.info('Realtime: Choice updated', { pollId, choiceId: payload.new.id })
                        choiceHandlersRef.current.handleChoiceUpdated(payload.new)
                    } else {
                        logger.warn('Realtime: Choice UPDATE event missing new data', { pollId })
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'votes'
                },
                (payload) => {
                    voteHandlersRef.current.processVoteInsert(payload)
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'votes'
                },
                (payload) => {
                    voteHandlersRef.current.processVoteUpdate(payload)
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'votes'
                },
                (payload) => {
                    voteHandlersRef.current.processVoteDelete(payload)
                }
            )
            .on('presence', { event: 'sync' }, () => {
                presenceHandlersRef.current.handlePresenceSync(channel)
            })
            .subscribe(async (status, err) => {
                logger.info('Realtime subscription status', { pollId, pollUuid, status, error: err })
                if (status === 'SUBSCRIBED') {
                    setRealtimeStatus('connected')
                    logger.info('Realtime subscription active', { pollId, pollUuid })
                    // Track this user's presence
                    try {
                        await channel.track({
                            device_id: deviceIdRef.current,
                            joined_at: new Date().toISOString()
                        })
                        logger.debug('Presence tracked', { pollId, deviceId: deviceIdRef.current })
                    } catch (presenceErr) {
                        logger.warn('Failed to track presence', { pollId, error: presenceErr })
                    }
                } else if (status === 'CLOSED') {
                    setRealtimeStatus('disconnected')
                    presenceHandlersRef.current.resetActiveViewers()
                    logger.warn('Realtime subscription closed', { pollId, pollUuid })
                } else if (status === 'CHANNEL_ERROR') {
                    setRealtimeStatus('error')
                    logger.error('Realtime subscription error', { pollId, pollUuid, error: err })
                } else if (status === 'TIMED_OUT') {
                    setRealtimeStatus('error')
                    logger.error('Realtime subscription timed out', { pollId, pollUuid })
                }
            })

        subscriptionRef.current = channel
    }, [pollId, pollUuid]) // Only depend on pollId and pollUuid - handlers are accessed via refs

    // Reconnect function for manual reconnection
    const reconnect = useCallback(() => {
        logger.info('Manual reconnection requested', { pollId })
        cleanupRealtimeSubscription()
        // Small delay to ensure cleanup completes
        setTimeout(() => {
            setupRealtimeSubscription()
        }, 100)
    }, [pollId, cleanupRealtimeSubscription, setupRealtimeSubscription])

    return {
        realtimeStatus,
        setupRealtimeSubscription,
        cleanupRealtimeSubscription,
        reconnect
    }
}

export default useRealtimeChannel
